// Leagues list — the operator's recurring league play at their primary
// facility (F6 Slice 1). Resolves the active facility (useResolvedFacility),
// then renders the table scoped to that facility. With no facility resolved we
// show a loading skeleton rather than an unscoped query or an empty-table lie.
//
// Renders ONLY real backend fields (LeagueResponse): name, format, status.
// league_id is a UUID — used solely as a React key and in the row's href, NEVER
// as visible text. No invented counts (a count == the loaded list length). No
// delete/archive affordance: there is no such endpoint in this slice.

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { notifications } from "@/app/notifications";
import { useResolvedFacility } from "@/hooks/use-selected-facility";
import {
  LEAGUE_FORMATS,
  createLeague,
  listLeagues,
  type LeagueFormat,
  type LeagueResponse,
  type LeagueStatus,
} from "@/libs/leaguesApi";
// Format/status display maps live in the module-local league-display module
// (shared with league-detail.tsx) so a label change lands in one place.
import { LEAGUE_FORMAT_LABELS, STATUS_LABELS } from "./league-display";

// Status filter options. "all" is a UI-only value; it is NEVER sent to the API.
const STATUS_FILTERS: { value: LeagueStatus | "all"; label: string }[] = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
];

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; leagues: LeagueResponse[] };

export function LeaguesList() {
  const { facilityId, isLoading: facilityLoading } = useResolvedFacility();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [statusFilter, setStatusFilter] = useState<LeagueStatus | "all">("all");

  // Create dialog state.
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [format, setFormat] = useState<LeagueFormat>("rotating_doubles");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  // Re-entrancy guard: a double-click fires createLeague at most once even
  // before React re-renders the disabled button.
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!facilityId) return;
    let cancelled = false;
    setState({ kind: "loading" });
    (async () => {
      try {
        const result = await listLeagues(
          facilityId,
          statusFilter === "all" ? undefined : statusFilter,
        );
        if (cancelled) return;
        setState({ kind: "ready", leagues: result });
      } catch (err) {
        if (!cancelled) {
          setState({
            kind: "error",
            message:
              err instanceof Error ? err.message : "Failed to load leagues.",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [facilityId, statusFilter]);

  const leagues = state.kind === "ready" ? state.leagues : [];
  // The "all"-statuses empty state surfaces its OWN create CTA, so the header
  // button is hidden there to avoid two identical "New league" controls — the
  // empty state then exposes exactly one.
  const showHeaderCreate = !(
    state.kind === "ready" &&
    leagues.length === 0 &&
    statusFilter === "all"
  );

  // Stable display order: most recently created first (defensive sort so the
  // list never reshuffles between renders, even if the server order changes).
  const sorted = useMemo(
    () =>
      [...leagues].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      ),
    [leagues],
  );

  const openCreate = () => {
    setName("");
    setFormat("rotating_doubles");
    setDescription("");
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!facilityId) return;
    const trimmed = name.trim();
    // Required-name gate — whitespace-only is rejected and never reaches the API.
    if (!trimmed) return;
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSaving(true);
    try {
      const created = await createLeague(facilityId, {
        name: trimmed,
        format,
        description: description.trim() || undefined,
      });
      // Append optimistically ONLY when the new league belongs in the current
      // view — i.e. the filter is "all" or the created status matches the active
      // filter. The backend always creates an "active" league, so appending
      // blindly while the "archived" filter is on would inject a filter-violating
      // row that lies about what the filter contains. When it does not match the
      // active filter we still notifications success + close; it simply isn't shown under
      // a filter it doesn't satisfy (and a later refetch keeps the list honest).
      if (statusFilter === "all" || created.status === statusFilter) {
        setState((prev) =>
          prev.kind === "ready"
            ? { kind: "ready", leagues: [...prev.leagues, created] }
            : { kind: "ready", leagues: [created] },
        );
      }
      notifications.success("League created.");
      setCreateOpen(false);
    } catch (err) {
      // Failure (incl. RBAC 403): honest notifications, dialog stays OPEN, no phantom row.
      notifications.error(
        err instanceof Error ? err.message : "Could not create the league.",
      );
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Stack spacing={3} sx={{ px: { xs: 2, lg: 3 }, pb: 2 }}>
      <Box>
        <Typography component="h1" sx={{ fontWeight: 700 }} variant="h5">
          Leagues
        </Typography>
        <Typography color="text.secondary" variant="body2">
          Recurring league play at this facility.
        </Typography>
      </Box>

      <Stack spacing={2}>
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ alignItems: "center", justifyContent: "space-between" }}
        >
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="league-status-filter-label">Status</InputLabel>
            <Select
              id="league-status-filter"
              label="Status"
              labelId="league-status-filter-label"
              onChange={(event) =>
                setStatusFilter(event.target.value as LeagueStatus | "all")
              }
              value={statusFilter}
            >
              {STATUS_FILTERS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {showHeaderCreate ? (
            <Button onClick={openCreate} size="small" variant="contained">
              New league
            </Button>
          ) : null}
        </Stack>

        {facilityLoading || !facilityId || state.kind === "loading" ? (
          <Stack aria-label="Loading leagues" role="status" spacing={1}>
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton
                height={48}
                key={index}
                variant="rounded"
                width="100%"
              />
            ))}
          </Stack>
        ) : state.kind === "error" ? (
          <Alert severity="error">{state.message}</Alert>
        ) : sorted.length === 0 ? (
          <Paper
            component="section"
            sx={{ borderStyle: "dashed", px: 2, py: 6, textAlign: "center" }}
            variant="outlined"
          >
            <Typography component="h2" variant="subtitle1">
              No leagues yet
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {statusFilter === "all"
                ? "Create your first league to get started."
                : "No leagues match this status filter."}
            </Typography>
            {statusFilter === "all" ? (
              <Button
                onClick={openCreate}
                size="small"
                sx={{ mt: 2 }}
                variant="contained"
              >
                New league
              </Button>
            ) : null}
          </Paper>
        ) : (
          <Paper sx={{ overflow: "hidden" }} variant="outlined">
            <Table aria-label="Leagues" size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Format</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((league) => (
                  <TableRow hover key={league.league_id}>
                    <TableCell>
                      <Link
                        component={RouterLink}
                        sx={{ fontWeight: 600 }}
                        to={`/leagues/${league.league_id}`}
                        underline="hover"
                      >
                        {league.name}
                      </Link>
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {LEAGUE_FORMAT_LABELS[league.format as LeagueFormat] ??
                        league.format}
                    </TableCell>
                    <TableCell>
                      <Chip
                        color={
                          league.status === "active" ? "success" : "default"
                        }
                        label={STATUS_LABELS[league.status] ?? league.status}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Stack>

      <Dialog
        aria-labelledby="new-league-title"
        onClose={() => {
          if (!saving) setCreateOpen(false);
        }}
        open={createOpen}
      >
        <DialogTitle id="new-league-title">New league</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              disabled={saving}
              id="league-name"
              label="Name"
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. Tuesday Doubles"
              required
              size="small"
              slotProps={{ htmlInput: { maxLength: 255 } }}
              value={name}
            />

            <FormControl disabled={saving} size="small">
              <InputLabel id="league-format-label">Format</InputLabel>
              <Select
                id="league-format"
                label="Format"
                labelId="league-format-label"
                onChange={(event) =>
                  setFormat(event.target.value as LeagueFormat)
                }
                value={format}
              >
                {LEAGUE_FORMATS.map((leagueFormat) => (
                  <MenuItem key={leagueFormat} value={leagueFormat}>
                    {LEAGUE_FORMAT_LABELS[leagueFormat]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              disabled={saving}
              id="league-description"
              label="Description"
              minRows={3}
              multiline
              onChange={(event) => setDescription(event.target.value)}
              placeholder="What this league is about, who it's for…"
              size="small"
              value={description}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={saving}
            onClick={() => setCreateOpen(false)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            disabled={saving || !name.trim()}
            onClick={() => void handleCreate()}
            size="small"
            variant="contained"
          >
            {saving ? "Creating…" : "Create league"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
